import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIconButton from '../components/DeleteIconButton';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { CREATE, DataAPIContext, DELETE, GET_LIST } from '../api';

// Deep-link to the chat conversation a thread is linked to. The channel id is encoded in the
// conversation's unique_name (…::<channelId>::…), same as the chat list / review pages.
const chatHrefFor = (uniqueName) => {
    const channelId = uniqueName?.split('::')[1];
    return channelId ? `/chat/${channelId}/conversations/${uniqueName}/interactivechat` : null;
};

// Manage the Airbnb_Chat_Thread rows attached to a single Tour_Operator_Airbnb_Account.
const AirbnbThreadsDialog = ({ account, open, onClose }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const [threads, setThreads] = React.useState([]);
    const [newThreadId, setNewThreadId] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    const loadThreads = React.useCallback(async () => {
        if (!account) return;
        setLoading(true);
        try {
            const res = await dataAPI(GET_LIST, 'airbnb_thread', {
                pagination: { page: 1, perPage: 200 },
                sort: { field: 'created_ts', order: 'DESC' },
                tour_operator_airbnb_account: account.id,
            });
            setThreads(res.data || []);
        } catch (e) {
            enqueueSnackbar('Failed to load threads', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [account, dataAPI, enqueueSnackbar]);

    React.useEffect(() => {
        if (open) loadThreads();
    }, [open, loadThreads]);

    const handleAdd = async () => {
        const threadId = newThreadId.trim();
        if (!threadId) return;
        try {
            const res = await dataAPI(CREATE, 'airbnb_thread', {
                data: { tour_operator_airbnb_account: account.id, airbnb_thread_id: threadId },
            });
            if (res.statusCode === 201) {
                enqueueSnackbar('Thread added', { variant: 'success' });
                setNewThreadId('');
                loadThreads();
            } else {
                enqueueSnackbar(res.data?.message || 'Failed to add thread', { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar('Failed to add thread', { variant: 'error' });
        }
    };

    const handleDelete = async (thread) => {
        try {
            await dataAPI(DELETE, 'airbnb_thread', { id: thread.id, data: {} });
            enqueueSnackbar('Thread removed', { variant: 'success' });
            loadThreads();
        } catch (e) {
            enqueueSnackbar('Failed to remove thread', { variant: 'error' });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Airbnb threads
                {account ? ` — ${account.airbnb_email}` : ''}
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Airbnb thread ID"
                        value={newThreadId}
                        onChange={(e) => setNewThreadId(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                    />
                    <Button variant="contained" onClick={handleAdd} disabled={!newThreadId.trim()}>
                        Add
                    </Button>
                </Stack>
                {threads.length === 0 && !loading ? (
                    <Typography variant="body2" color="text.secondary">
                        No threads yet. Most threads are created automatically from incoming Airbnb
                        messages; add one here only if you need to seed it manually.
                    </Typography>
                ) : (
                    <List dense>
                        {threads.map((t) => {
                            const chatHref = chatHrefFor(t.conversation?.unique_name);
                            return (
                                <ListItem
                                    key={t.id}
                                    secondaryAction={
                                        <DeleteIconButton title="Delete thread" edge="end" size="medium" fontSize="medium" onClick={() => handleDelete(t)} />
                                    }
                                >
                                    <ListItemText
                                        primary={t.airbnb_thread_id}
                                        secondary={
                                            <>
                                                {chatHref ? (
                                                    <Link
                                                        component="button"
                                                        variant="body2"
                                                        onClick={() => {
                                                            onClose();
                                                            history.push(chatHref);
                                                        }}
                                                    >
                                                        View conversation
                                                    </Link>
                                                ) : (
                                                    'No linked conversation'
                                                )}
                                                {t.created_ts
                                                    ? ` · ${new Date(t.created_ts).toLocaleString()}`
                                                    : ''}
                                            </>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AirbnbThreadsDialog;
